import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { scale, webHeader, width } from '@components/frame';
import { isPhone, workerType, bytesToView, isWeb, Popup } from '@helpers';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import { getBoxShadowStyle } from '@styles/shadow';
import TouchableElement from '@components/common/touchableElement';
import Header from '@components/settings/v2/common/SettingsHeader';

import ConnectorStatus from '@components/settings/v2/structure/connector/fileConnector/components/connectorStatus';

import { requestForActionOnError } from '@reducers/fileConnector';
import { BACK_BUTTON_SETTINGS_TEXT, SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';

const ConnectorBlock = ({ C, data, fileConnectorStatus, connected, totalFiles, totalSize, triggerInfoPopUp }) => {
  const horizontal = [
    {
      key: 'headers',
      values: [
        {
          boldText: 'NC files',
        },
        {
          boldText: 'Production linked files',
          width: 2,
        },
        {
          boldText: 'Other files',
        },
      ],
    },
    {
      key: 'completedLine',
      values: [
        {
          size: data.totalNcFileSizeCompleted,
          files: data.totalNcFileCountCompleted,
          status: 'completed',
        },
        {
          size: data.totalErpFileSizeCompleted,
          files: data.totalErpFileCountCompleted,
          width: 2,
          status: 'completed',
        },
        {
          size: data.totalOtherFileSizeCompleted,
          files: data.totalOtherFileCountCompleted,
          status: 'completed',
        },
      ],
    },
    {
      key: 'remainingLine',
      values: [
        {
          size: data.totalNcFileSizeRemaining,
          files: data.totalNcFileCountRemaining,
          status: 'remaining',
        },
        {
          size: data.totalErpFileSizeRemaining,
          files: data.totalErpFileCountRemaining,
          width: 2,
          status: 'remaining',
        },
        {
          size: data.totalOtherFileSizeRemaining,
          files: data.totalOtherFileCountRemaining,
          status: 'remaining',
        },
      ],
    },
    {
      key: 'failedLine',
      values: [
        {
          size: data.totalNcFileSizeFailed,
          files: data.totalNcFileCountFailed,
          status: 'failed',
          moreIcon: true,
        },
        {
          size: data.totalErpFileSizeFailed,
          files: data.totalErpFileCountFailed,
          width: 2,
          status: 'failed',
        },
        {
          size: data.totalOtherFileSizeFailed,
          files: data.totalOtherFileCountFailed,
          status: 'failed',
        },
      ],
    },
    {
      name: 'New NC file downloaded from machine',
      values: [
        {
          size: data.totalNcFileSizeDownloadRemaining,
          files: data.totalNcFileCountDownloadRemaining,
          number: 1,
        },
        {
          topText: 'Linked to Active',
          bottomText: 'Production Orders',
        },
        {
          topText: 'Linked to Inactive',
          bottomText: 'Production Orders',
        },
        {
          empty: true,
        },
      ],
    },
    {
      name: 'File Watcher event',
      values: [
        {
          size: data.totalNcFileSizeFolderWatcherRemaining,
          files: data.totalNcFileCountFolderWatcherRemaining,
          number: 2,
        },
        {
          size: data.totalErpFileSizeWatcherRemaining,
          files: data.totalErpFileCountWatcherRemaining,
          number: 3,
          width: 2,
        },
        {
          size: data.totalOtherFileSizeWatcherRemaining,
          files: data.totalOtherFileCountWatcherRemaining,
          number: 9,
        },
      ],
    },
    {
      name: 'Production linked files hot',
      values: [
        {
          empty: true,
        },
        {
          size: data.totalErpFileSizeHotActiveRemaining,
          files: data.totalErpFileCountHotActiveRemaining,
          number: 4,
        },
        {
          size: data.totalErpFileSizeHotInActiveRemaining,
          files: data.totalErpFileCountHotInActiveRemaining,
          number: 10,
        },
        {
          empty: true,
        },
      ],
    },
    {
      name: 'Folder scan',
      values: [
        {
          size: data.totalNcFileSizeFolderScanRemaining,
          files: data.totalNcFileCountFolderScanRemaining,
          number: 5,
        },
        {
          size: data.totalErpFileSizeFolderScanRemaining,
          files: data.totalErpFileCountFolderScanRemaining,
          number: 6,
          width: 2,
        },
        {
          size: data.totalOtherFileSizeScanRemaining,
          files: data.totalOtherFileCountScanRemaining,
          number: 13,
        },
      ],
    },
    {
      name: 'Production linked files cold',
      values: [
        {
          empty: true,
        },
        {
          size: data.totalErpFileSizeColdActiveRemaining,
          files: data.totalErpFileCountColdActiveRemaining,
          number: 7,
        },
        {
          size: data.totalErpFileSizeColdInActiveRemaining,
          files: data.totalErpFileCountColdInActiveRemaining,
          number: 12,
        },
        {
          empty: true,
        },
      ],
    },
  ];

  return (
    <View
      style={[
        getBoxShadowStyle({ type: 'Field', C }),
        {
          width: '100%',
          maxWidth: scale(844),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          backgroundColor: C('Background/Light/Field'),
        },
      ]}
    >
      <ConnectorStatus {...{ connected, C, totalBytesSentPerSecond: data.totalBytesSentPerSecond, fileConnectorStatus, totalFiles, totalSize }} />
      <View style={{ paddingBottom: scale(18), paddingHorizontal: scale(22), marginTop: scale(27) }}>
        {horizontal.map((h, index) => {
          const firstLine = index === 0;
          const lastLine = index === horizontal.length - 1;
          const isEmpty = !h.name;
          return (
            <View key={h.name || h.key} style={{ flexDirection: 'row' }}>
              <View
                style={[
                  {
                    borderTopWidth: scale(isEmpty ? 0 : 1),
                    borderLeftWidth: scale(isEmpty ? 0 : 1),
                    borderBottomWidth: lastLine ? scale(1) : 0,
                    borderColor: C('Hairline/Light/No trans'),
                    width: scale(260),
                    height: scale(50),
                    justifyContent: 'center',
                  },
                ]}
              >
                <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), marginLeft: scale(4) }]}>{h.name}</Text>
              </View>
              {h.values.map((v, i) => {
                const lastValue = i === h.values.length - 1;
                return (
                  <View
                    key={(h.name || h.key) + 'value' + i}
                    style={[
                      {
                        borderTopWidth: firstLine ? 0 : scale(1),
                        borderLeftWidth: firstLine ? 0 : scale(1),
                        borderBottomWidth: lastLine ? scale(1) : 0,
                        borderRightWidth: !firstLine && lastValue ? scale(1) : 0,
                        borderColor: C('Hairline/Light/No trans'),
                        width: scale(135) * (v.width || 1),
                        height: scale(50),
                      },
                    ]}
                  >
                    {v.moreIcon && v.size > 0 && (
                      <TouchableElement onPress={triggerInfoPopUp}>
                        {({ boxStyle }) => (
                          <View style={[boxStyle, { position: 'absolute', right: scale(6), top: scale(6), zIndex: 10 }]}>
                            <Svg icon="more" width={scale(20)} height={scale(20)} color={'Button/Light/Primary'} />
                          </View>
                        )}
                      </TouchableElement>
                    )}
                    {v.boldText && (
                      <View style={{ height: '100%', justifyContent: 'center' }}>
                        <Text style={[font['Button 3'], { color: C('Label/Light/Primary'), marginLeft: scale(4) }]}>{i18n.t(v.boldText)}</Text>
                      </View>
                    )}
                    {v.topText && (
                      <Text style={[font['Small'], { color: C('Label/Light/Primary'), textAlign: 'center', marginTop: scale(3) }]}>
                        {i18n.t(v.topText)}
                      </Text>
                    )}
                    {v.bottomText && (
                      <Text style={[font['Small'], { color: C('Label/Light/Primary'), textAlign: 'center', marginTop: scale(-5) }]}>
                        {i18n.t(v.bottomText)}
                      </Text>
                    )}
                    {v.size >= 0 && (
                      <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), marginLeft: scale(4), marginTop: scale(3) }]}>
                        {bytesToView(v.size)}
                      </Text>
                    )}
                    {v.files >= 0 && (
                      <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), marginLeft: scale(4) }]}>
                        {v.files} {i18n.t('files')}
                      </Text>
                    )}
                    {(v.size >= 0 || v.files >= 0) && (
                      <Text style={[font['Small'], { color: C('Label/Light/Tertiary'), marginLeft: scale(4), marginTop: scale(-3) }]}>
                        {i18n.t(v.status || 'remaining')}
                      </Text>
                    )}
                    {v.number && (
                      <Text style={[font['Small'], { color: C('Label/Light/Tertiary'), position: 'absolute', right: scale(5), top: scale(1) }]}>
                        {v.number}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const FileConnector = (props) => {
  const {
    C,
    connector: {
      connectorDetails: { status, version },
    },
    fileConnector: { updatedAt, data, connected },
    requestForActionOnError,
  } = props;

  const [showInfoPopUp, setInfoPopUp] = useState(false);
  const triggerInfoPopUp = () => setInfoPopUp(!showInfoPopUp);

  const frameTopPosition = scale(105) + scale(50) * 3 + scale(27);
  const frameLeftPosition = scale(22) + scale(260);

  const totalFiles = data.totalNcFileCountRemaining + data.totalErpFileCountRemaining + data.totalOtherFileCountRemaining;
  const totalSize = data.totalNcFileSizeRemaining + data.totalErpFileSizeRemaining + data.totalOtherFileSizeRemaining;

  const size = data.totalNcFileSizeFailed;
  const files = data.totalNcFileCountFailed;

  const fileConnectorStatus = status.find((i) => i.workerType === workerType.FILES) || {};

  return [
    showInfoPopUp && (
      <InfoModal
        {...{
          connected,
          C,
          fileConnectorStatus,
          totalFiles,
          totalSize,
          frameTopPosition,
          frameLeftPosition,
          files,
          size,
          requestForActionOnError,
          hide: triggerInfoPopUp,
        }}
        key="infoModal"
      />
    ),
    <Header backBtnText={BACK_BUTTON_SETTINGS_TEXT} name={'FileConnectorDetails'} key={'FileConnectorDetailsHeader'} />,
    <View
      style={{
        height: '100%',
        width: '100%',
        backgroundColor: C('Background/Light/Detail'),
        alignItems: 'center',
        paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
      }}
      key="contentComponent"
    >
      <ConnectorBlock
        {...{
          C,
          version,
          fileConnectorStatus,
          updatedAt,
          connected,
          totalFiles,
          totalSize,
          data,
          triggerInfoPopUp,
        }}
      />
    </View>,
  ];
};

const InfoModal = ({ C, frameTopPosition, frameLeftPosition, files, size, hide, requestForActionOnError }) => {
  const actions = [
    {
      text: 'Retry failed files',
      icon: 'reload',
      color: 'Label/Light/Primary',
      width: 14,
      height: 17,
      action: () => requestForActionOnError('RetryAllErrors'),
    },
    {
      text: 'Download log file',
      icon: 'download',
      color: 'Label/Light/Primary',
      width: 20,
      height: 18,
      action: () => requestForActionOnError('DownloadErrorLog'),
    },
    {
      text: 'Remove from list',
      icon: 'remove',
      color: 'Label/Light/Warning',
      width: 16,
      height: 16,
      action: () => requestForActionOnError('DeleteAllErrors'),
    },
  ];

  return (
    <TouchableElement onPress={hide}>
      {({ boxStyle }) => (
        <View
          style={[
            {
              position: 'absolute',
              width: '100%',
              height: '100%',
              alignItems: 'center',
              zIndex: 10000,
              backgroundColor: C('Overlay/Light/Dim strong'),
            },
            boxStyle,
          ]}
        >
          <View style={{ width, height: '100%', alignItems: 'center' }}>
            <View
              style={{
                width: '100%',
                maxWidth: scale(844),
                marginTop: frameTopPosition,
              }}
            >
              <View style={{ height: scale(42) + (isPhone ? scale(7) : scale(12)) * 2 }}></View>
              <View
                style={{
                  marginLeft: frameLeftPosition + scale(1),
                }}
              >
                <View
                  style={{
                    width: scale(134),
                    height: scale(49),
                    marginTop: scale(1),
                  }}
                >
                  <View
                    style={{
                      width: scale(134),
                      height: scale(49),
                      borderRadius: scale(4),
                      backgroundColor: C('Background/Light/Field'),
                    }}
                  >
                    <TouchableElement onPress={hide}>
                      {({ boxStyle }) => (
                        <View style={[boxStyle, { position: 'absolute', right: scale(6), top: scale(6), zIndex: 20 }]}>
                          <Svg icon="more" width={scale(20)} height={scale(20)} color={'Button/Light/Secondary'} />
                        </View>
                      )}
                    </TouchableElement>
                    {size >= 0 && (
                      <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), marginLeft: scale(4), marginTop: scale(3) }]}>
                        {bytesToView(size)}
                      </Text>
                    )}
                    {files >= 0 && (
                      <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), marginLeft: scale(4) }]}>
                        {files} {i18n.t('files')}
                      </Text>
                    )}
                    {(size >= 0 || files >= 0) && (
                      <Text style={[font['Small'], { color: C('Label/Light/Tertiary'), marginLeft: scale(4), marginTop: scale(-3) }]}>
                        {i18n.t('failed')}
                      </Text>
                    )}
                  </View>
                  <View
                    style={{
                      position: 'absolute',
                      top: scale(54),
                      right: 0,
                    }}
                  >
                    <View
                      style={{
                        width: scale(250),
                        height: scale(123),
                        backgroundColor: C('Background/Light/Field'),
                        borderRadius: scale(4),
                      }}
                    >
                      {actions.map((i, index) => (
                        <Line {...{ ...i, C, isLast: index === actions.length - 1, isFirst: index === 0, hide }} key={i.icon} />
                      ))}
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
      )}
    </TouchableElement>
  );
};

const Line = ({ C, text, icon, color, action, isLast, isFirst, width, height, hide }) => {
  return (
    <TouchableElement
      onPress={async () => {
        if (action) {
          await action();
          hide();
        }
      }}
      hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
    >
      {({ boxStyle, pressHoverColor }) => (
        <View style={[boxStyle]}>
          <View
            style={[
              {
                width: '100%',
                height: scale(41),
                backgroundColor: C(pressHoverColor('Background/Light/Field')),
                borderBottomColor: C('Hairline/Light/No trans'),
                borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth,
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: scale(20),
                justifyContent: 'space-between',
              },
              isFirst && {
                borderTopLeftRadius: scale(4),
                borderTopRightRadius: scale(4),
              },
              isLast && {
                borderBottomLeftRadius: scale(4),
                borderBottomRightRadius: scale(4),
              },
            ]}
          >
            <Text style={[font.Body, { color: C(color) }]}>{i18n.t(text)}</Text>
            <Svg icon={icon} width={scale(width)} height={scale(height)} color={color} />
          </View>
        </View>
      )}
    </TouchableElement>
  );
};
export default connect(({ connector, fileConnector }) => ({ connector, fileConnector }), {
  requestForActionOnError,
})(ColorWrapper(FileConnector));
